import { useEffect } from "react";
import { Link } from "react-router-dom";
import { getOpenJobs, CAREERS_EMAIL } from "@/data/jobsData";
import "@/pages/Careers.css";

/* Role-card label overrides. The detail pages keep their full
   "Department · Location · Type · Experience" meta line; the
   listing cards show a tighter, candidate-facing set. */
const ROLE_CARD_TAGS = {
  "junior-designer": ["2\u20133 years experience", "Bangalore", "Full-time"],
  "design-intern": [
    "Recent architecture / interior design graduate",
    "Bangalore",
    "Full-time",
  ],
};

export default function Careers() {
  const openJobs = getOpenJobs();

  useEffect(() => {
    document.title = "Careers — Ōraṇi";
    window.scrollTo(0, 0);
  }, []);

  const scrollToOpenRoles = (e) => {
    e.preventDefault();
    const el = document.getElementById("open-roles");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <main className="cr" data-testid="careers-page" aria-label="Careers">
      {/* HERO */}
      <header className="cr__hero" data-testid="cr-hero">
        <span className="cr__eyebrow">Careers</span>
        <h1 className="cr__title" data-testid="cr-title">
          Help us push the boundaries
          <br className="cr__title-break" aria-hidden="true" />
          {" "}of what design can be.
        </h1>
        <div className="cr__intro" data-testid="cr-intro">
          <p className="cr__intro-lead">
            Ōraṇi began with interiors and
            architecture. Now, we are asking a larger question: what
            if a studio could design not just a place, but the way it
            is experienced?
          </p>
          <p className="cr__intro-tail">
            Not just the space itself, but the behaviours it shapes,
            the rituals it makes room for, the atmosphere it creates,
            and the way it is remembered.
          </p>
          <p className="cr__intro-close" data-testid="cr-intro-close">
            Join us if you want to think deeply, make carefully, and
            help build a studio for what design becomes next.
          </p>
        </div>
        <a
          href="#open-roles"
          onClick={scrollToOpenRoles}
          className="cr__hero-cta"
          data-testid="cr-see-roles"
        >
          See open roles
          <span aria-hidden="true" className="cr__rule" />
        </a>
      </header>

      {/* OPEN ROLES */}
      <section
        id="open-roles"
        className="cr__roles"
        data-testid="cr-roles"
        aria-label="Open positions"
      >
        <span className="cr__eyebrow cr__eyebrow--block">Open positions</span>
        <h2 className="cr__roles-title">Current openings</h2>

        {openJobs.length > 0 ? (
          <ul className="cr__role-list" data-testid="cr-role-list">
            {openJobs.map((job) => (
              <li key={job.slug}>
                <Link
                  to={`/careers/${job.slug}`}
                  className="cr__role"
                  data-testid={`cr-role-${job.slug}`}
                >
                  <div className="cr__role-main">
                    <h3 className="cr__role-title">{job.title}</h3>
                    <p className="cr__role-meta">
                      {(ROLE_CARD_TAGS[job.slug] || [
                        job.location,
                        job.type,
                        job.experience,
                      ].filter(Boolean)).map((tag, idx, arr) => (
                        <span key={tag}>
                          {tag}
                          {idx < arr.length - 1 ? (
                            <span aria-hidden="true"> · </span>
                          ) : null}
                        </span>
                      ))}
                    </p>
                    <p className="cr__role-summary">{job.listingSummary}</p>
                  </div>
                  <span className="cr__role-cta" aria-hidden="true">
                    View role
                    <svg width="22" height="10" viewBox="0 0 22 10">
                      <path
                        d="M1 5h20M17 1l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1"
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="cr__no-roles" data-testid="cr-no-roles">
            No openings at the moment. Write to us — we are always glad
            to meet thoughtful designers and makers.
          </p>
        )}
      </section>

      {/* GENERAL APPLICATION */}
      <section
        className="cr__general"
        data-testid="cr-general"
        aria-label="General application"
      >
        <h2 className="cr__general-title">Don’t see the right opening?</h2>
        <p className="cr__general-body">
          Send us your portfolio and a short note. We are always open
          to meeting people who care about interiors, materials, craft,
          styling, and the making of homes.
        </p>
        <a
          href={`mailto:${CAREERS_EMAIL}?subject=Portfolio%20%E2%80%94%20%C5%8Cra%E1%B9%87i`}
          className="cr__general-cta"
          data-testid="cr-general-cta"
        >
          Write to us
          <span aria-hidden="true" className="cr__rule" />
        </a>
        <span className="cr__general-email" data-testid="cr-general-email">
          {CAREERS_EMAIL}
        </span>
      </section>
    </main>
  );
}
